import { type ReactElement, Suspense, useMemo, useState } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { AuthRouteTransitionFallback } from '@/pages/auth/AuthRouteTransitionFallback';
import { NotFoundScreen } from '@/pages/NotFoundScreen';
import { layoutSizes } from '@/theme/layoutSizes';
import { s } from '@/theme/tokens';
import { Panel } from '@/ui/composites/Panel';
import {
  PageTitleOverrideProvider,
  type PageMetadataOptions,
  usePageMetadata,
} from '@/ui/hooks/usePageTitle';
import { Section } from '@/ui/layout/Section';
import { Stack } from '@/ui/layout/Stack';
import { Box, Text } from '@/ui/primitives';
import { Button } from '@/ui/primitives/Button';
import { PageShell } from '@/ui/primitives/PageShell';
import {
  RequireAdminAccess,
  RequireAuth,
  RequireFeatureFlag,
  RequireOnboarding,
  RequireSuperuserAccess,
} from './guards/RequireAuth';
import { RequireDesktop } from './guards/RequireDesktop';
import { RouteErrorBoundary } from './RouteErrorBoundary';
import type { AppRouteDefinition } from './routes';
import type { AuthError, AuthSession } from '@/auth/types';
import type { OrganisationDto, ProjectDto } from '@flowqa/shared';

type AppRoutesProps = {
  routeDefinitions: AppRouteDefinition[];
  featureFlagsLoading?: boolean;
  featureFlags?: Record<string, boolean> | null;
  authBypassClerk: boolean;
  session: AuthSession | null;
  authLoading: boolean;
  projectsLoaded: boolean;
  authError: AuthError | null;
  signIn: (input: { email: string; password: string }) => Promise<void>;
  signUp: (input: {
    email: string;
    password: string;
    firstName?: string;
    lastName?: string;
    acceptedPolicies: boolean;
  }) => Promise<void>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<boolean>;
  setSessionDirect: (next: AuthSession | null) => void;
  isAdmin: boolean;
  adminAccessLoading: boolean;
  isSuperuser: boolean;
  organisations: Array<Pick<OrganisationDto, 'id' | 'name' | 'slug'>>;
  projects: ProjectDto[];
  onAddProject: () => void;
};

const publicContentFallbackRouteKeys = new Set([
  'landing',
  'preview',
  'public-features',
  'plans',
  'use-cases',
  'privacy-requests',
]);

const isPublicContentFallbackRoute = (routeKey?: string): boolean =>
  routeKey !== undefined &&
  (publicContentFallbackRouteKeys.has(routeKey) || routeKey.startsWith('use-case-'));

const PublicContentRouteFallback = (): ReactElement => (
  <Box
    aria-hidden
    data-public-route-fallback="true"
    sx={{
      minHeight: `calc(100dvh - ${String(layoutSizes.topBar.height)}px)`,
    }}
  />
);

const resolveRouteFallback = (routeKey?: string): ReactElement | null => {
  if (routeKey === 'sign-in') {
    return <AuthRouteTransitionFallback title="Sign In" />;
  }
  if (routeKey === 'sign-up') {
    return <AuthRouteTransitionFallback title="Sign Up" />;
  }
  if (isPublicContentFallbackRoute(routeKey)) {
    return <PublicContentRouteFallback />;
  }
  return null;
};

export const AppRoutes = ({
  routeDefinitions,
  featureFlagsLoading,
  featureFlags,
  authBypassClerk,
  session,
  authLoading,
  projectsLoaded,
  authError,
  signIn,
  signUp,
  signOut,
  refreshSession,
  setSessionDirect,
  isAdmin,
  adminAccessLoading,
  isSuperuser,
  organisations,
  projects,
  onAddProject,
}: AppRoutesProps): ReactElement => {
  const location = useLocation();
  const { selectedOrganisationId } = useOrganisationFilter();
  const adminAccess = {
    canAccess: isAdmin,
    loading: adminAccessLoading,
  };
  const allowMobileRouteKeys = new Set([
    'landing',
    'public-features',
    'use-cases',
    'sign-in',
    'sign-up',
    'auth-callback',
    'session-expired',
  ]);
  const isMobileAllowedRoute = (routeKey: string): boolean =>
    allowMobileRouteKeys.has(routeKey) || routeKey.startsWith('use-case-');
  const wrapRoute = (
    element: ReactElement,
    routeKey?: string,
    title?: string,
    description?: string,
    metadata?: PageMetadataOptions,
  ): ReactElement => (
    <RouteErrorBoundary routeKey={routeKey}>
      <PageTitle title={title} description={description} metadata={metadata}>
        <Suspense fallback={resolveRouteFallback(routeKey)}>{element}</Suspense>
      </PageTitle>
    </RouteErrorBoundary>
  );

  const routeContext = {
    session,
    authLoading,
    authError,
    signIn,
    signUp,
    signOut,
    refreshSession,
    setSessionDirect,
    isAdmin,
    organisations,
    projects,
    onAddProject,
  };
  const noProjectsGateRouteKeys = new Set([
    'execution-runs',
    'uploads',
    'suites',
    'tests',
    'pipelines',
    'stages',
    'jobs',
    'features',
    'releases',
  ]);
  const selectedOrganisationExists =
    selectedOrganisationId !== null &&
    organisations.some((organisation) => organisation.id === selectedOrganisationId);
  const hasProjectsForSelectedOrganisation =
    selectedOrganisationId !== null &&
    projects.some((project) => project.organisationId === selectedOrganisationId);

  return (
    <Routes key={location.pathname} location={location}>
      {routeDefinitions
        .filter((route) => route.enabled ?? true)
        .filter((route) => !(route.devOnly && !import.meta.env.DEV))
        .map((route) => {
          let element = route.element(routeContext);
          const shouldShowNoProjectsGate =
            route.requiresAuth === true &&
            noProjectsGateRouteKeys.has(route.key) &&
            authError === null &&
            projectsLoaded &&
            selectedOrganisationExists &&
            !hasProjectsForSelectedOrganisation;
          if (shouldShowNoProjectsGate) {
            element = <NoProjectsListGate onAddProject={onAddProject} />;
          }
          if (route.requiresAuth || route.requiresAdmin || route.requiresSuperuser) {
            element = (
              <RequireAuth
                session={session}
                loading={authLoading}
                authError={authError}
                refreshSession={refreshSession}
                featureFlagsLoading={featureFlagsLoading}
                featureFlags={featureFlags}
                authBypassClerk={authBypassClerk}
              >
                {element}
              </RequireAuth>
            );
          }
          if (route.requiresAuth || route.requiresAdmin || route.requiresSuperuser) {
            element = <RequireOnboarding session={session}>{element}</RequireOnboarding>;
          }
          if (route.featureFlag) {
            element = (
              <RequireFeatureFlag
                featureFlag={route.featureFlag}
                featureFlagsLoading={featureFlagsLoading}
                featureFlags={featureFlags}
              >
                {element}
              </RequireFeatureFlag>
            );
          }
          if (route.requiresAdmin) {
            element = (
              <RequireAdminAccess canAccess={adminAccess.canAccess} loading={adminAccess.loading}>
                {element}
              </RequireAdminAccess>
            );
          }
          if (route.requiresSuperuser) {
            element = (
              <RequireSuperuserAccess canAccess={isSuperuser}>{element}</RequireSuperuserAccess>
            );
          }
          element = (
            <RequireDesktop allowOnMobile={isMobileAllowedRoute(route.key)}>
              {element}
            </RequireDesktop>
          );
          return (
            <Route
              key={route.key}
              path={route.path}
              element={wrapRoute(
                element,
                route.key,
                route.title,
                route.description,
                route.metadata,
              )}
            />
          );
        })}
      <Route
        path="*"
        element={wrapRoute(
          <RequireDesktop>
            <NotFoundScreen />
          </RequireDesktop>,
          'not-found',
          'Page Not Found',
          undefined,
          undefined,
        )}
      />
    </Routes>
  );
};

const NoProjectsListGate = ({ onAddProject }: { onAddProject: () => void }): ReactElement => (
  <PageShell maxWidth="xs" paddingX="lg" paddingY="xl" paddingBottom="xl">
    <Stack gap="xl" sx={{ pt: s('5xl') }}>
      <Section>
        <Box sx={{ width: '100%' }}>
          <Panel
            title="No Projects Found"
            titleSx={{ width: '100%', textAlign: 'center' }}
            actionsAlign="center"
            actions={<Button onClick={onAddProject}>Add Project</Button>}
          >
            <Text role="bodyMuted" align="center">
              Add a project to start adding test results.
            </Text>
          </Panel>
        </Box>
      </Section>
    </Stack>
  </PageShell>
);

type PageTitleProps = { title?: string; children: ReactElement };

type PageTitleWithDescriptionProps = PageTitleProps & {
  description?: string;
  metadata?: PageMetadataOptions;
};

const PageTitle = ({
  title,
  description,
  metadata,
  children,
}: PageTitleWithDescriptionProps): ReactElement => {
  const [titleOverride, setTitleOverride] = useState<string | undefined>(undefined);
  const overrideContext = useMemo(() => ({ setTitleOverride }), []);

  usePageMetadata(titleOverride ?? title, description, metadata);

  return <PageTitleOverrideProvider value={overrideContext}>{children}</PageTitleOverrideProvider>;
};
